import type { CanvasItemType, CanvasLayoutItemUpsert } from '@/types/canvas';
import type { CanvasLogicalGroup } from '@/lib/canvas/canvas-reference-graph';
import type {
  DialogueVoiceoverResponse,
  StoryboardSoundEffectResponse,
} from '@/services/api/storyboard-resources';
import type { SoundEffectGenerationTask } from '@/stores/soundEffectGenerationStore';
import { getDisplayableSoundEffectLocalTasks } from '@/lib/canvas/storyboard-audio-generation-tasks';

import type { StoryboardCanvasDialogue, StoryboardCanvasNode } from './node-types';
import {
  ARRANGED_ROW_HEIGHT,
  DEFAULT_STORYBOARD_AUDIO_COLLECTION_X,
  DEFAULT_STORYBOARD_VOICEOVER_X,
  START_Y,
  STORYBOARD_AUDIO_COLLECTION_NODE_FALLBACK_HEIGHT,
  STORYBOARD_AUDIO_COLLECTION_NODE_WIDTH,
} from './layout-constants';

/**
 * 音频逻辑组尺寸（与历史版本组同思路：组框与条目为并列节点，绝对坐标 + padding 隔开）。
 * 条目宽高为固定值，避免卡片真实高度撑破导致互相遮盖。
 * 不再使用 RF parentId / extent:'parent'。
 * 组名渲染在组框**外侧**上方（CanvasGroupFrameHeader），故框内顶部不再为标题预留高度。
 */
export const STORYBOARD_AUDIO_GROUP_PADDING = 28;

export const STORYBOARD_AUDIO_GROUP_ITEM_GAP = 20;

export const STORYBOARD_AUDIO_GROUP_PADDING_BOTTOM = 28;

/** 组内条目固定宽度（组宽 - 左右 padding）。 */
export const STORYBOARD_AUDIO_ITEM_WIDTH = 404;

/** 对白条目固定高度：序号/头像 + 可滚对白区 + 情绪常驻位 + 选中内边距（D8-ux3）。 */
export const STORYBOARD_AUDIO_DIALOGUE_ITEM_HEIGHT = 200;

/** 配音条目固定高度：卡外表头（序号/角色/类型/音色）+ 紧凑波形播放器 + 选中内边距。 */
export const STORYBOARD_AUDIO_VOICEOVER_ITEM_HEIGHT = 168;

/** 音效条目固定高度：SoundEffectItem（含底部元信息栏）+ 选中内边距。 */
export const STORYBOARD_AUDIO_SOUND_EFFECT_ITEM_HEIGHT = 184;

/** 组内无业务条目时的占位卡高度（与配音满卡同高）。 */
export const STORYBOARD_AUDIO_GROUP_EMPTY_BODY_HEIGHT = STORYBOARD_AUDIO_VOICEOVER_ITEM_HEIGHT;

export const STORYBOARD_AUDIO_GROUP_CREATE_BUTTON_SLOT = 48;

/** @deprecated 使用 STORYBOARD_AUDIO_GROUP_PADDING */
export const STORYBOARD_AUDIO_GROUP_PADDING_X = STORYBOARD_AUDIO_GROUP_PADDING;

export const DIALOGUE_NODE_Z_INDEX_OFFSET = 300;

export const VOICEOVER_NODE_Z_INDEX_OFFSET = 400;

export const SOUND_EFFECT_NODE_Z_INDEX_OFFSET = 500;

export type StoryboardAudioCollectionLayoutItemType = Extract<
  CanvasItemType,
  'storyboard_dialogue' | 'storyboard_voiceover' | 'storyboard_sound_effect'
>;

export const STORYBOARD_AUDIO_COLLECTION_ORDER: StoryboardAudioCollectionLayoutItemType[] = [
  'storyboard_dialogue',
  'storyboard_voiceover',
  'storyboard_sound_effect',
];

export const STORYBOARD_AUDIO_COLLECTION_Z_INDEX_BY_ITEM_TYPE: Record<
  StoryboardAudioCollectionLayoutItemType,
  number
> = {
  storyboard_dialogue: DIALOGUE_NODE_Z_INDEX_OFFSET,
  storyboard_voiceover: VOICEOVER_NODE_Z_INDEX_OFFSET,
  storyboard_sound_effect: SOUND_EFFECT_NODE_Z_INDEX_OFFSET,
};

export function getStoryboardAudioCollectionZIndex(
  itemType: StoryboardAudioCollectionLayoutItemType
): number {
  return STORYBOARD_AUDIO_COLLECTION_Z_INDEX_BY_ITEM_TYPE[itemType];
}

export function getStoryboardAudioCollectionPosition(
  itemType: StoryboardAudioCollectionLayoutItemType,
  storyboardId: string,
  rowIndex: number
): CanvasLayoutItemUpsert {
  // D4-audio-abc-columns：A=对白/音效会话 hub，B=对白/音效组，C=配音组；写死边允许斜向。
  // rowIndex 仅保留兼容，实际 y 按类型固定。
  void rowIndex;
  const x =
    itemType === 'storyboard_voiceover'
      ? DEFAULT_STORYBOARD_VOICEOVER_X
      : DEFAULT_STORYBOARD_AUDIO_COLLECTION_X;
  const y = itemType === 'storyboard_sound_effect' ? START_Y + ARRANGED_ROW_HEIGHT : START_Y;
  return {
    itemType,
    itemId: storyboardId,
    x,
    y,
    zIndex: getStoryboardAudioCollectionZIndex(itemType),
  };
}

export function sortStoryboardDialogues(
  dialogues: StoryboardCanvasDialogue[]
): StoryboardCanvasDialogue[] {
  return [...dialogues].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
}

export function sortStoryboardVoiceovers(
  voiceovers: DialogueVoiceoverResponse[]
): DialogueVoiceoverResponse[] {
  return [...voiceovers].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function sortStoryboardSoundEffects(
  soundEffects: StoryboardSoundEffectResponse[]
): StoryboardSoundEffectResponse[] {
  return [...soundEffects].sort((a, b) => {
    if (a.effectType !== b.effectType) return a.effectType - b.effectType;
    return b.version - a.version;
  });
}

/** 对白/配音/音效逻辑组 RF className：与历史组框同级，抬升由 canvas-group-stack-* 处理。 */
export const CANVAS_AUDIO_GROUP_NODE_CLASSNAME = 'canvas-audio-group-node';

export function getStoryboardDialogueNodeId(storyboardId: string): string {
  return `storyboard-dialogues:${storyboardId}`;
}

export function getStoryboardVoiceoverNodeId(storyboardId: string): string {
  return `storyboard-voiceovers:${storyboardId}`;
}

export function getStoryboardSoundEffectNodeId(storyboardId: string): string {
  return `storyboard-sound-effects:${storyboardId}`;
}

/** 对白/配音/音效逻辑组节点 id（整理时会话 hub 走 ABC 列，不进右侧历史列）。 */
export function isStoryboardAudioCollectionNodeId(nodeId: string): boolean {
  return (
    nodeId.startsWith('storyboard-dialogues:') ||
    nodeId.startsWith('storyboard-voiceovers:') ||
    nodeId.startsWith('storyboard-sound-effects:')
  );
}

/**
 * 整理 occupied 初始占位应跳过的音频相关节点（组框 + 条目）。
 * 仅跳过组框不够：骨架同顶时配音条目已在 B 列顶，会变成「幽灵 occupied」把对白 hub 顶下去。
 */
export function isStoryboardAudioArrangeOccupancyNodeId(nodeId: string): boolean {
  if (isStoryboardAudioCollectionNodeId(nodeId)) return true;
  return (
    nodeId.startsWith('storyboard-dialogue-item:') ||
    nodeId.startsWith('storyboard-voiceover-item:') ||
    nodeId.startsWith('storyboard-sound-effect-item:')
  );
}

export function readStoryboardIdFromCanvasNodeData(data: unknown): string {
  if (!data || typeof data !== 'object' || !('storyboardId' in data)) return '';
  const value = (data as { storyboardId?: unknown }).storyboardId;
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 选中音频条目时，额外把所属逻辑组标为 tools-active，以便组上 GenerationBox 仍展示。
 * 仅返回 companion（组）id，不含 activeNodeId 自身。
 *
 * 音效条目除外：音效生成是整组行为（无「针对某条音效生成」语义），生成框只在音效组自身激活时展示。
 */
export function resolveStoryboardAudioToolsCompanionNodeIds(
  activeNodeId: string,
  nodes: ReadonlyArray<{
    id: string;
    type?: string | null;
    data?: unknown;
  }>
): string[] {
  const active = nodes.find((node) => node.id === activeNodeId);
  if (!active) return [];
  if (active.type === 'storyboardSoundEffectItem') return [];
  const groupNodeId = resolveStoryboardAudioItemGroupNodeId(active);
  return groupNodeId ? [groupNodeId] : [];
}

/**
 * 由对白/配音/音效**条目**节点解析所属逻辑组 id（生成框组件仍挂在组上，故组需一起 tools-active）。
 * 非音频条目返回 null。
 */
export function resolveStoryboardAudioItemGroupNodeId(node: {
  type?: string | null;
  data?: unknown;
}): string | null {
  const storyboardId = readStoryboardIdFromCanvasNodeData(node.data);
  if (!storyboardId) return null;

  switch (node.type) {
    case 'storyboardDialogueItem':
      return getStoryboardDialogueNodeId(storyboardId);
    case 'storyboardVoiceoverItem':
      return getStoryboardVoiceoverNodeId(storyboardId);
    case 'storyboardSoundEffectItem':
      return getStoryboardSoundEffectNodeId(storyboardId);
    default:
      return null;
  }
}

export function getStoryboardDialogueItemNodeId(dialogueId: string): string {
  return `storyboard-dialogue-item:${dialogueId}`;
}

/** 组内无对白时的空占位条目 id（稳定、不落库）。 */
export function getStoryboardDialogueEmptyPlaceholderItemNodeId(storyboardId: string): string {
  return `storyboard-dialogue-item:placeholder:${storyboardId}`;
}

export function isStoryboardDialogueEmptyPlaceholderItemNodeId(nodeId: string): boolean {
  return nodeId.startsWith('storyboard-dialogue-item:placeholder:');
}

/** 从对白条目节点 id 取回 dialogueId（placeholder 无对应对白，返回 null）。 */
export function parseStoryboardDialogueItemNodeId(
  nodeId: string | null | undefined
): string | null {
  if (!nodeId || !nodeId.startsWith('storyboard-dialogue-item:')) return null;
  if (isStoryboardDialogueEmptyPlaceholderItemNodeId(nodeId)) return null;
  const dialogueId = nodeId.slice('storyboard-dialogue-item:'.length).trim();
  return dialogueId || null;
}

export function getStoryboardVoiceoverItemNodeId(voiceoverId: string): string {
  return `storyboard-voiceover-item:${voiceoverId}`;
}

/** 无激活配音时的空槽节点 id（由 dialogueId 稳定推导，条目不落库）。 */
export function getStoryboardVoiceoverPlaceholderItemNodeId(dialogueId: string): string {
  return `storyboard-voiceover-item:placeholder:${dialogueId}`;
}

export function isStoryboardVoiceoverPlaceholderItemNodeId(nodeId: string): boolean {
  return nodeId.startsWith('storyboard-voiceover-item:placeholder:');
}

/** 无对白时配音组空占位（整组「暂无配音」）。 */
export function getStoryboardVoiceoverEmptyPlaceholderItemNodeId(storyboardId: string): string {
  return `storyboard-voiceover-item:placeholder:empty:${storyboardId}`;
}

export function getStoryboardSoundEffectItemNodeId(soundEffectId: string): string {
  return `storyboard-sound-effect-item:${soundEffectId}`;
}

/** 音效本地生成占位节点 id（由 task.localId 稳定推导）。 */
export function getStoryboardSoundEffectPlaceholderItemNodeId(localId: string): string {
  return `storyboard-sound-effect-item:placeholder:${localId}`;
}

export function isStoryboardSoundEffectPlaceholderItemNodeId(nodeId: string): boolean {
  return nodeId.startsWith('storyboard-sound-effect-item:placeholder:');
}

/** 组内无音效时的空占位条目 id。 */
export function getStoryboardSoundEffectEmptyPlaceholderItemNodeId(storyboardId: string): string {
  return `storyboard-sound-effect-item:placeholder:empty:${storyboardId}`;
}

/** 空占位条目用的合成对白（不落库、不可编辑）。 */
export function createStoryboardAudioEmptyPlaceholderDialogue(
  storyboardId: string
): StoryboardCanvasDialogue {
  return {
    dialogueId: `__empty__:${storyboardId}`,
    storyboardId,
    content: '',
    dialogueType: 1,
    sequenceOrder: 0,
  };
}

export function getActiveStoryboardVoiceovers(
  voiceovers: DialogueVoiceoverResponse[]
): DialogueVoiceoverResponse[] {
  return voiceovers.filter((voiceover) => voiceover.isActive);
}

/** 配音组槽位：与对白一一对应（有激活配音显示音频，无则空槽）。 */
export interface StoryboardVoiceoverSlot {
  dialogue: StoryboardCanvasDialogue;
  voiceover: DialogueVoiceoverResponse | null;
  nodeId: string;
  index: number;
}

export function buildStoryboardVoiceoverSlots(
  dialogues: StoryboardCanvasDialogue[],
  voiceovers: DialogueVoiceoverResponse[]
): StoryboardVoiceoverSlot[] {
  const activeByDialogueId = new Map<string, DialogueVoiceoverResponse>();
  for (const voiceover of getActiveStoryboardVoiceovers(voiceovers)) {
    if (!activeByDialogueId.has(voiceover.dialogueId)) {
      activeByDialogueId.set(voiceover.dialogueId, voiceover);
    }
  }

  return sortStoryboardDialogues(dialogues).map((dialogue, index) => {
    const active = activeByDialogueId.get(dialogue.dialogueId) ?? null;
    return {
      dialogue,
      voiceover: active,
      nodeId: active
        ? getStoryboardVoiceoverItemNodeId(active.voiceoverId)
        : getStoryboardVoiceoverPlaceholderItemNodeId(dialogue.dialogueId),
      index,
    };
  });
}

/**
 * D8-fix2：由对白 id 解析配音槽条目节点 id（有激活配音用真实 id，否则 placeholder）。
 * 「生成配音」应对焦该条目，而非配音组（避免整组 selected 橙框）。
 */
export function resolveStoryboardVoiceoverItemNodeIdForDialogue(
  dialogueId: string,
  dialogues: StoryboardCanvasDialogue[],
  voiceovers: DialogueVoiceoverResponse[]
): string {
  const slot = buildStoryboardVoiceoverSlots(dialogues, voiceovers).find(
    (item) => item.dialogue.dialogueId === dialogueId
  );
  return slot?.nodeId ?? getStoryboardVoiceoverPlaceholderItemNodeId(dialogueId);
}

export function getStoryboardAudioGroupSize(options: {
  itemCount: number;
  itemHeight: number;
  includeCreateButtonSlot?: boolean;
}): { width: number; height: number } {
  const { itemCount, itemHeight, includeCreateButtonSlot = false } = options;
  const bodyHeight =
    itemCount <= 0
      ? STORYBOARD_AUDIO_GROUP_EMPTY_BODY_HEIGHT
      : itemCount * itemHeight + (itemCount - 1) * STORYBOARD_AUDIO_GROUP_ITEM_GAP;
  const createSlot = includeCreateButtonSlot ? STORYBOARD_AUDIO_GROUP_CREATE_BUTTON_SLOT : 0;
  return {
    width: STORYBOARD_AUDIO_COLLECTION_NODE_WIDTH,
    height:
      STORYBOARD_AUDIO_GROUP_PADDING +
      bodyHeight +
      createSlot +
      STORYBOARD_AUDIO_GROUP_PADDING_BOTTOM,
  };
}

/** 条目绝对坐标 = 组原点 + 内边距 + 列表偏移（永不落库）。 */
export function getStoryboardAudioItemAbsolutePosition(
  groupPosition: { x: number; y: number },
  index: number,
  itemHeight: number
): { x: number; y: number } {
  return {
    x: groupPosition.x + STORYBOARD_AUDIO_GROUP_PADDING,
    y:
      groupPosition.y +
      STORYBOARD_AUDIO_GROUP_PADDING +
      index * (itemHeight + STORYBOARD_AUDIO_GROUP_ITEM_GAP),
  };
}

export function getStoryboardAudioItemWidth(): number {
  return STORYBOARD_AUDIO_ITEM_WIDTH;
}

export type StoryboardDialogueCreateAnchorNode = {
  position: { x: number; y: number };
  measured?: { width?: number; height?: number };
  width?: number;
  height?: number;
  style?: { width?: number | string; height?: number | string };
};

function readPositiveCssSize(value: number | string | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

/**
 * 「新增对白」表单节点贴对白组框底部：x 与组内条目对齐，y 在组高下方再留一条目间距。
 * 组高：style.height（构建时写入）→ measured.height → node.height → fallback。
 */
export function resolveStoryboardDialogueCreateNodePosition(
  groupNode: StoryboardDialogueCreateAnchorNode
): { x: number; y: number } {
  const height =
    readPositiveCssSize(groupNode.style?.height) ??
    readPositiveCssSize(groupNode.measured?.height) ??
    readPositiveCssSize(groupNode.height) ??
    STORYBOARD_AUDIO_COLLECTION_NODE_FALLBACK_HEIGHT;

  return {
    x: groupNode.position.x + STORYBOARD_AUDIO_GROUP_PADDING,
    y: groupNode.position.y + height + STORYBOARD_AUDIO_GROUP_ITEM_GAP,
  };
}

export type StoryboardAudioLogicalGroupRelayoutSpec = {
  groupType: 'storyboardDialogueGroup' | 'storyboardVoiceoverGroup' | 'storyboardSoundEffectGroup';
  itemType: 'storyboardDialogueItem' | 'storyboardVoiceoverItem' | 'storyboardSoundEffectItem';
  itemHeight: number;
  getItemIndex: (node: StoryboardCanvasNode) => number;
};

export const STORYBOARD_AUDIO_LOGICAL_GROUP_RELAYOUT_SPECS: readonly StoryboardAudioLogicalGroupRelayoutSpec[] =
  [
    {
      groupType: 'storyboardDialogueGroup',
      itemType: 'storyboardDialogueItem',
      itemHeight: STORYBOARD_AUDIO_DIALOGUE_ITEM_HEIGHT,
      getItemIndex: (node) =>
        node.type === 'storyboardDialogueItem' ? node.data.index : Number.MAX_SAFE_INTEGER,
    },
    {
      groupType: 'storyboardVoiceoverGroup',
      itemType: 'storyboardVoiceoverItem',
      itemHeight: STORYBOARD_AUDIO_VOICEOVER_ITEM_HEIGHT,
      getItemIndex: (node) =>
        node.type === 'storyboardVoiceoverItem' ? node.data.index : Number.MAX_SAFE_INTEGER,
    },
    {
      groupType: 'storyboardSoundEffectGroup',
      itemType: 'storyboardSoundEffectItem',
      itemHeight: STORYBOARD_AUDIO_SOUND_EFFECT_ITEM_HEIGHT,
      getItemIndex: (node) =>
        node.type === 'storyboardSoundEffectItem' ? node.data.index : Number.MAX_SAFE_INTEGER,
    },
  ];

/**
 * 音频逻辑组专用重排：以组框当前位置为锚点，重算全部条目绝对坐标。
 * 用于本地布局应用、节点同步合并、进入画布初始化（对齐历史版本组 relayout）。
 */
export function relayoutStoryboardAudioLogicalGroupNodes(
  nodes: readonly StoryboardCanvasNode[]
): StoryboardCanvasNode[] {
  if (nodes.length === 0) return [...nodes];

  const nextPositions = new Map<string, { x: number; y: number }>();

  for (const spec of STORYBOARD_AUDIO_LOGICAL_GROUP_RELAYOUT_SPECS) {
    const groupNode = nodes.find((node) => node.type === spec.groupType);
    if (!groupNode) continue;

    const itemNodes = nodes
      .filter((node) => node.type === spec.itemType)
      .sort((left, right) => spec.getItemIndex(left) - spec.getItemIndex(right));

    itemNodes.forEach((itemNode, index) => {
      nextPositions.set(
        itemNode.id,
        getStoryboardAudioItemAbsolutePosition(groupNode.position, index, spec.itemHeight)
      );
    });
  }

  if (nextPositions.size === 0) return [...nodes];

  return nodes.map((node) => {
    const position = nextPositions.get(node.id);
    if (!position) return node;
    if (node.position.x === position.x && node.position.y === position.y) return node;
    return { ...node, position };
  });
}

/**
 * 音频逻辑组（对白/配音/音效）：拖任一成员或组框时整组平移。
 * 与历史版本 `CanvasLogicalGroup` 同结构。
 */
export function buildStoryboardAudioLogicalGroups(options: {
  storyboardId: string;
  dialogues: StoryboardCanvasDialogue[];
  voiceovers: DialogueVoiceoverResponse[];
  soundEffects: StoryboardSoundEffectResponse[];
  soundEffectLocalTasks?: SoundEffectGenerationTask[];
}): CanvasLogicalGroup[] {
  const { storyboardId, dialogues, voiceovers, soundEffects, soundEffectLocalTasks = [] } = options;
  const sortedDialogues = sortStoryboardDialogues(dialogues);
  const groups: CanvasLogicalGroup[] = [];

  const dialogueGroupId = getStoryboardDialogueNodeId(storyboardId);
  groups.push({
    id: dialogueGroupId,
    memberIds: [
      dialogueGroupId,
      ...(sortedDialogues.length > 0
        ? sortedDialogues.map((dialogue) => getStoryboardDialogueItemNodeId(dialogue.dialogueId))
        : [getStoryboardDialogueEmptyPlaceholderItemNodeId(storyboardId)]),
    ],
  });

  const voiceoverSlots = buildStoryboardVoiceoverSlots(dialogues, voiceovers);
  const voiceoverGroupId = getStoryboardVoiceoverNodeId(storyboardId);
  groups.push({
    id: voiceoverGroupId,
    memberIds: [
      voiceoverGroupId,
      ...(voiceoverSlots.length > 0
        ? voiceoverSlots.map((slot) => slot.nodeId)
        : [getStoryboardVoiceoverEmptyPlaceholderItemNodeId(storyboardId)]),
    ],
  });

  const soundEffectGroupId = getStoryboardSoundEffectNodeId(storyboardId);
  const sortedSoundEffects = sortStoryboardSoundEffects(soundEffects);
  const displayableSoundEffectTasks = getDisplayableSoundEffectLocalTasks(
    soundEffectLocalTasks,
    sortedSoundEffects
  );
  const soundEffectMemberIds = [
    ...displayableSoundEffectTasks.map((task) =>
      getStoryboardSoundEffectPlaceholderItemNodeId(task.localId)
    ),
    ...sortedSoundEffects.map((soundEffect) =>
      getStoryboardSoundEffectItemNodeId(soundEffect.soundEffectId)
    ),
  ];
  groups.push({
    id: soundEffectGroupId,
    memberIds: [
      soundEffectGroupId,
      ...(soundEffectMemberIds.length > 0
        ? soundEffectMemberIds
        : [getStoryboardSoundEffectEmptyPlaceholderItemNodeId(storyboardId)]),
    ],
  });

  return groups;
}
